import { createContext, useContext, useEffect, useState, useRef, useCallback, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useListQuery } from "@/hooks/useListQuery";
import { propertyStorageKey } from "@/lib/homeControlCentre/brands";
import {
  trackDashboardView, trackFunnelStage, deriveHomeownerStage,
} from "@/lib/homeownerTracking";

export interface CustomerJob {
  id: string;
  service: string;
  region: string | null;
  status: string;
  created_at: string;
  scheduled_start: string | null;
  scheduled_end: string | null;
  installer_id: string | null;
}

export interface ProposalStatus {
  jobId: string;
  status: string;
  valid_until: string | null;
}

export interface PreDealSummary {
  properties: Array<{
    id: string;
    address_line_1: string | null;
    postcode: string | null;
    property_type?: string | null;
  }>;
  leads: Array<{ id: string; service: string; status: string; created_at: string; region: string | null }>;
  enquiries: Array<{ id: string; service: string | null; created_at: string; message: string | null }>;
}

export interface InsightContext {
  jobs: CustomerJob[];
  proposalStatuses: Record<string, ProposalStatus>;
  docCount: number;
  hasProperty: boolean;
  epcRating: string | null;
  interests: Array<{ service_category: string; priority: string; timeframe?: string | null }>;
  enquiryCount: number;
  lastCommDate: string | null;
}

interface CustomerHubState {
  customerName: string;
  customerId: string;
  firstName: string;
  list: ReturnType<typeof useListQuery<CustomerJob>>;
  jobIds: string[];
  docCount: number;
  proposalStatuses: Record<string, ProposalStatus>;
  preDeal: PreDealSummary | null;
  preDealLoading: boolean;
  interests: Array<{ service_category: string; priority: string; timeframe?: string | null }>;
  lastCommDate: string | null;
  epcRating: string | null;
  showOnboarding: boolean;
  setShowOnboarding: (v: boolean) => void;
  handleOnboardingComplete: () => Promise<void>;
  refreshHub: () => Promise<void>;
  hasJobs: boolean;
  activeCount: number;
  approvalCount: number;
  urgentJob: CustomerJob | undefined;
  property: PreDealSummary["properties"][0] | undefined;
  /** Every property this homeowner owns or is linked to. */
  properties: PreDealSummary["properties"];
  selectedPropertyId: string | null;
  setSelectedPropertyId: (id: string) => void;
  /** job id -> property id, so projects can be scoped to the selected home. */
  jobPropertyMap: Record<string, string | null>;
  userId: string | null;
  isLoading: boolean;
  insightContext: InsightContext;
  greeting: string;
}

const CustomerHubCtx = createContext<CustomerHubState | null>(null);

export function useCustomerHub() {
  const ctx = useContext(CustomerHubCtx);
  if (!ctx) throw new Error("useCustomerHub must be used inside CustomerHubProvider");
  return ctx;
}

export function CustomerHubProvider({ children }: { children: ReactNode }) {
  const [searchParams] = useSearchParams();
  const [customerName, setCustomerName] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [docCount, setDocCount] = useState(0);
  const [proposalStatuses, setProposalStatuses] = useState<Record<string, ProposalStatus>>({});
  const [jobIds, setJobIds] = useState<string[]>([]);
  const [preDeal, setPreDeal] = useState<PreDealSummary | null>(null);
  const [preDealLoading, setPreDealLoading] = useState(true);
  const [interests, setInterests] = useState<Array<{ service_category: string; priority: string; timeframe?: string | null }>>([]);
  const [lastCommDate, setLastCommDate] = useState<string | null>(null);
  const [propertyEpc, setPropertyEpc] = useState<Record<string, string | null>>({});
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [allProperties, setAllProperties] = useState<PreDealSummary["properties"]>([]);
  const [jobPropertyMap, setJobPropertyMap] = useState<Record<string, string | null>>({});
  const [userId, setUserId] = useState<string | null>(null);
  const [selectedPropertyId, setSelectedPropertyIdState] = useState<string | null>(null);

  /**
   * Monotonic load generation plus the account each load belongs to. A late
   * response from a previous account or a superseded load is discarded rather
   * than written over fresher state.
   */
  const loadGenerationRef = useRef(0);
  const loadedUserRef = useRef<string | null>(null);


  const list = useListQuery<CustomerJob>({ rpcName: "customer_list_jobs", scope: "customer_jobs" });

  const load = useCallback(async () => {
      const generation = ++loadGenerationRef.current;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const uid = session.user.id;
      const current = () => loadGenerationRef.current === generation;

      // A different account is signed in: drop everything the previous
      // account left behind rather than showing it beside new data.
      if (loadedUserRef.current && loadedUserRef.current !== uid) {
        setCustomerName(""); setCustomerId(""); setDocCount(0);
        setProposalStatuses({}); setJobIds([]); setPreDeal(null);
        setInterests([]); setLastCommDate(null); setPropertyEpc({});
        setAllProperties([]); setJobPropertyMap({});
        setSelectedPropertyIdState(null); setPreDealLoading(true);
      }
      loadedUserRef.current = uid;
      setUserId(uid);

      // Bump member activity (debounced via sessionStorage to once per hour)
      try {
        const last = Number(sessionStorage.getItem("member_activity_bumped_at") ?? 0);
        if (Date.now() - last > 60 * 60 * 1000) {
          sessionStorage.setItem("member_activity_bumped_at", String(Date.now()));
          await supabase.rpc("bump_member_activity", { _score_delta: 1 });
        }
      } catch { /* ignore */ }

      const needsOnboarding = searchParams.get("onboarding") === "true"
        || !session.user.user_metadata?.onboarding_completed;

      const { data: cust } = await supabase
        .from("customers").select("id, full_name, onboarding_completed")
        .eq("user_id", uid).maybeSingle();
      if (!current()) return;
      if (cust?.full_name) setCustomerName(cust.full_name);
      if (cust?.id) {
        setCustomerId(cust.id);
        if (needsOnboarding && !cust.onboarding_completed) {
          setShowOnboarding(true);
        }
      } else {
        const { data: member } = await supabase
          .from("homeowner_members")
          .select("full_name")
          .eq("user_id", uid)
          .maybeSingle();
        if (!current()) return;
        if (member?.full_name) setCustomerName(member.full_name);
        if (needsOnboarding) setShowOnboarding(true);
      }

      // Plans are owned by the signed-in homeowner, so members without a
      // customer record can record and see future projects too.
      const { data: intData } = await supabase
        .from("customer_project_interests")
        .select("service_category, priority, timeframe, status, target_date")
        .eq("user_id", uid);
      if (!current()) return;
      if (intData) setInterests(intData);


      const { data: pd } = await supabase.rpc("customer_pre_deal_summary");
      if (!current()) return;
      if (pd) setPreDeal(pd as unknown as PreDealSummary);
      setPreDealLoading(false);

      // Owned properties first, then any resolved through the pre-deal summary.
      const { data: userProps } = await supabase
        .from("properties")
        .select("id, address_line1, postcode, epc_rating, property_type")
        .eq("user_id", uid)
        .order("updated_at", { ascending: false });
      if (!current()) return;

      // Keep one entry per property so the switcher never duplicates a home.
      {
        const merged: PreDealSummary["properties"] = [];
        const seen = new Set<string>();
        const epc: Record<string, string | null> = {};
        for (const p of userProps ?? []) {
          if (seen.has(p.id)) continue;
          seen.add(p.id);
          epc[p.id] = (p.epc_rating as string | null) ?? null;
          merged.push({
            id: p.id,
            address_line_1: (p.address_line1 as string) ?? null,
            postcode: (p.postcode as string) ?? null,
            property_type: (p.property_type as string | null) ?? null,
          });
        }
        for (const p of ((pd as unknown as PreDealSummary)?.properties ?? [])) {
          if (!p?.id || seen.has(p.id)) continue;
          seen.add(p.id);
          // The pre-deal summary carries the rating recorded on that exact
          // property. No postcode matching: a neighbour's certificate is not
          // this homeowner's certificate.
          epc[p.id] = ((p as { epc_rating?: string | null }).epc_rating ?? null);
          merged.push(p);
        }
        setAllProperties(merged);
        setPropertyEpc(epc);
      }

      const { data: links } = await supabase
        .from("job_customers").select("job_id")
        .eq("customer_user_id", uid);
      if (!current()) return;
      if (links && links.length > 0) {
        const ids = links.map((l) => l.job_id);
        setJobIds(ids);
        const { data: jobRows } = await supabase.from("jobs").select("id, property_id").in("id", ids);
        if (!current()) return;
        if (jobRows) {
          const map: Record<string, string | null> = {};
          for (const j of jobRows) map[j.id] = (j.property_id as string | null) ?? null;
          setJobPropertyMap(map);
        }
        const [{ data: docs }, { data: proposals }, { data: lastComm }] = await Promise.all([
          supabase.from("job_documents").select("id").in("job_id", ids).eq("customer_visible", true),
          supabase.from("customer_proposals").select("job_id, status, valid_until").in("job_id", ids).in("status", ["sent", "viewed", "accepted", "rejected"]).order("version", { ascending: false }),
          supabase.from("comm_log").select("created_at").in("job_id", ids).order("created_at", { ascending: false }).limit(1),
        ]);
        if (!current()) return;
        setDocCount(docs?.length ?? 0);
        if (lastComm?.[0]?.created_at) setLastCommDate(lastComm[0].created_at);
        const pMap: Record<string, ProposalStatus> = {};
        for (const p of proposals || []) {
          if (!pMap[p.job_id]) {
            pMap[p.job_id] = { jobId: p.job_id, status: p.status, valid_until: p.valid_until };
          }
        }
        setProposalStatuses(pMap);
      } else {
        setJobIds([]); setJobPropertyMap({}); setDocCount(0); setProposalStatuses({});
      }
    }, [searchParams]);


  useEffect(() => { void load(); }, [load]);

  const handleOnboardingComplete = useCallback(async () => {
    setShowOnboarding(false);
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      const { data: intData } = await supabase
        .from("customer_project_interests")
        .select("service_category, priority, timeframe, status, target_date")
        .eq("user_id", session.user.id);
      if (intData) setInterests(intData);
    }
    const { data: pd } = await supabase.rpc("customer_pre_deal_summary");
    if (pd) setPreDeal(pd as unknown as PreDealSummary);
  }, []);


  const hasJobs = list.rows.length > 0;
  const activeCount = list.rows.filter(j => !["approved_for_payment", "paid", "completed"].includes(j.status)).length;
  const approvalCount = list.rows.filter(j => j.status === "ready_for_signoff").length;
  const urgentJob = list.rows.find(j => j.status === "ready_for_signoff") ?? list.rows.find(j => j.status === "in_progress");
  const properties = allProperties.length > 0 ? allProperties : (preDeal?.properties ?? []);

  // Restore the homeowner's last selected home, per signed-in user.
  useEffect(() => {
    if (!userId || properties.length === 0) return;
    setSelectedPropertyIdState((current) => {
      if (current && properties.some((p) => p.id === current)) return current;
      let stored: string | null = null;
      try { stored = localStorage.getItem(propertyStorageKey(userId)); } catch { /* ignore */ }
      if (stored && properties.some((p) => p.id === stored)) return stored;
      return properties[0]?.id ?? null;
    });
  }, [userId, properties]);

  const setSelectedPropertyId = useCallback((id: string) => {
    setSelectedPropertyIdState(id);
    try { localStorage.setItem(propertyStorageKey(userId), id); } catch { /* ignore */ }
  }, [userId]);

  const property = properties.find((p) => p.id === selectedPropertyId) ?? properties[0];
  // The rating belongs to the selected home only — never to another property.
  const epcRating = property ? (propertyEpc[property.id] ?? null) : null;

  const firstName = customerName?.split(" ")[0] || "";
  const isLoading = list.loading && list.rows.length === 0 && preDealLoading;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  const insightContext: InsightContext = {
    jobs: list.rows,
    proposalStatuses,
    docCount,
    hasProperty: !!property,
    epcRating,
    interests,
    enquiryCount: (preDeal?.enquiries?.length ?? 0) + (preDeal?.leads?.length ?? 0),
    lastCommDate,
  };

  // Tracking
  const trackedRef = useRef(false);
  useEffect(() => {
    if (isLoading || trackedRef.current) return;
    trackedRef.current = true;
    const stage = hasJobs ? (activeCount > 0 ? "active" : "completed") : "pre_deal";
    trackDashboardView({ hasJobs, activeCount, stage });
    const funnelStage = deriveHomeownerStage({
      hasJobs,
      hasInterests: interests.length > 0,
      hasEnquiry: insightContext.enquiryCount > 0,
      statuses: list.rows.map(j => j.status),
    });
    trackFunnelStage(funnelStage);
  }, [isLoading, hasJobs, activeCount, interests, preDeal, list.rows]);

  const value: CustomerHubState = {
    customerName, customerId, firstName, list, jobIds, docCount, proposalStatuses,
    preDeal, preDealLoading, interests, lastCommDate, epcRating,
    showOnboarding, setShowOnboarding, handleOnboardingComplete, refreshHub: load,
    hasJobs, activeCount, approvalCount, urgentJob, property, isLoading,
    properties, selectedPropertyId: property?.id ?? null, setSelectedPropertyId,
    jobPropertyMap, userId,
    insightContext, greeting,
  };

  return <CustomerHubCtx.Provider value={value}>{children}</CustomerHubCtx.Provider>;
}
